import { Handle, Position } from '@xyflow/react';
import {
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Layout,
  List,
  MessageSquare,
  MousePointer2,
  Phone,
  Video,
} from 'lucide-react';

const MessageNode = ({ data }) => {
  const type = data.type || 'text';
  const buttons = data.buttons || [];

  const getHeaderInfo = () => {
    switch (type) {
      case 'text':
        return {
          icon: <MessageSquare size={14} className="text-blue-500" />,
          label: 'Text Message',
          color: 'bg-blue-50 border-blue-100 text-blue-700',
        };
      case 'image':
        return {
          icon: <ImageIcon size={14} className="text-purple-500" />,
          label: 'Image',
          color: 'bg-purple-50 border-purple-100 text-purple-700',
        };
      case 'video':
        return {
          icon: <Video size={14} className="text-red-500" />,
          label: 'Video',
          color: 'bg-red-50 border-red-100 text-red-700',
        };
      case 'document':
        return {
          icon: <FileText size={14} className="text-orange-500" />,
          label: 'Document',
          color: 'bg-orange-50 border-orange-100 text-orange-700',
        };
      case 'buttons':
        return {
          icon: <MousePointer2 size={14} className="text-green-500" />,
          label: 'Buttons',
          color: 'bg-green-50 border-green-100 text-green-700',
        };
      case 'list':
        return {
          icon: <List size={14} className="text-indigo-500" />,
          label: 'List Menu',
          color: 'bg-indigo-50 border-indigo-100 text-indigo-700',
        };
      case 'template':
        return {
          icon: <Layout size={14} className="text-emerald-500" />,
          label: 'WhatsApp Template',
          color: 'bg-emerald-50 border-emerald-100 text-emerald-700',
        };
      default:
        return {
          icon: <MessageSquare size={14} />,
          label: 'Message',
          color: 'bg-gray-50 border-gray-100 text-gray-700',
        };
    }
  };

  const info = getHeaderInfo();

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-72 overflow-hidden transition-all hover:shadow-2xl">
      <div
        className={`p-3 flex items-center justify-between border-b ${info.color}`}
      >
        <div className="flex items-center gap-2">
          {info.icon}
          <span className="text-xs font-bold uppercase tracking-widest">
            {info.label}
          </span>
        </div>
      </div>

      <div className="p-5">
        {type === 'buttons' &&
          data.headerType &&
          data.headerType !== 'none' && (
            <div className="mb-3 rounded-xl overflow-hidden border border-gray-100 bg-gray-50 flex flex-col items-center justify-center min-h-[60px]">
              {data.headerType === 'text' && (
                <div className="p-3 w-full bg-gray-100/50 border-b border-gray-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Header Text
                  </p>
                  <p className="text-xs font-bold text-gray-800">
                    {data.headerText || 'No header text set'}
                  </p>
                </div>
              )}
              {data.headerType === 'image' && (
                <div className="aspect-video w-full flex flex-col items-center justify-center text-gray-400 bg-gray-100/50">
                  <ImageIcon size={24} className="mb-1" />
                  <span className="text-[10px] font-bold uppercase tracking-tight">
                    Image Header
                  </span>
                  {data.headerMediaId && (
                    <span className="text-[8px] mt-1 opacity-60">
                      ID: {data.headerMediaId}
                    </span>
                  )}
                </div>
              )}
              {data.headerType === 'video' && (
                <div className="aspect-video w-full flex flex-col items-center justify-center text-gray-400 bg-gray-100/50">
                  <Video size={24} className="mb-1" />
                  <span className="text-[10px] font-bold uppercase tracking-tight">
                    Video Header
                  </span>
                  {data.headerMediaId && (
                    <span className="text-[8px] mt-1 opacity-60">
                      ID: {data.headerMediaId}
                    </span>
                  )}
                </div>
              )}
              {data.headerType === 'document' && (
                <div className="p-3 w-full flex items-center gap-3 text-gray-400 bg-gray-100/50">
                  <FileText size={20} />
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-tight">
                      Document Header
                    </span>
                    {data.headerMediaId && (
                      <span className="text-[8px] opacity-60">
                        ID: {data.headerMediaId}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        {(type === 'text' || type === 'buttons' || !type) && (
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
            <p className="text-sm text-gray-700 leading-relaxed italic whitespace-pre-wrap">
              {data.message ? `"${data.message}"` : '"No content set"'}
            </p>
            {type === 'buttons' && data.footer && (
              <p className="mt-2 text-[10px] text-gray-400 font-medium border-t border-gray-100 pt-1">
                {data.footer}
              </p>
            )}
          </div>
        )}

        {(type === 'image' || type === 'video' || type === 'document') && (
          <div className="aspect-video bg-gray-100 rounded-xl border border-dashed border-gray-300 flex flex-col items-center justify-center p-4">
            {type === 'image' && (
              <ImageIcon size={32} className="text-gray-300 mb-2" />
            )}
            {type === 'video' && (
              <Video size={32} className="text-gray-300 mb-2" />
            )}
            {type === 'document' && (
              <FileText size={32} className="text-gray-300 mb-2" />
            )}
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              {type} preview
            </span>
          </div>
        )}

        {type === 'buttons' && buttons.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 pl-1">
              Interactive Buttons
            </div>
            {buttons.map(button => (
              <div
                key={button.id}
                className="relative p-2.5 bg-white border border-gray-200 rounded-xl text-center text-xs font-semibold text-gray-700 shadow-sm"
              >
                {button.label}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={button.id}
                  style={{ top: '50%' }}
                  className="!w-4 !h-4 !bg-emerald-500 !border-[3px] !border-white !-right-2 transition-all hover:scale-125 hover:!bg-emerald-600 shadow-md"
                />
              </div>
            ))}
          </div>
        )}

        {type === 'template' && data.template && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 mb-3">
            {/* Template Header Preview */}
            {data.template.header && (
              <div className="bg-gray-100 p-2 border-b border-gray-200 flex items-center gap-2 text-gray-500">
                {data.template.header.format === 'TEXT' ? (
                  <span className="text-[10px] font-bold uppercase truncate">
                    {data.template.header.text}
                  </span>
                ) : (
                  <>
                    {data.template.header.format === 'IMAGE' && <ImageIcon size={12} />}
                    {data.template.header.format === 'VIDEO' && <Video size={12} />}
                    {data.template.header.format === 'DOCUMENT' && <FileText size={12} />}
                    <span className="text-[10px] font-bold uppercase">
                      {data.template.header.format}
                    </span>
                  </>
                )}
              </div>
            )}
            
            {/* Template Body Preview */}
            <div className="p-3">
              <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed line-clamp-3 italic">
                {data.template.body?.text || 'No template body'}
              </p>
              {data.template.footer && (
                <p className="text-[10px] text-gray-400 mt-1">
                  {data.template.footer.text}
                </p>
              )}
            </div>

            {/* Template Buttons & Handles */}
            {data.template.buttons && data.template.buttons.length > 0 && (
              <div className="border-t border-gray-200">
                {data.template.buttons.map((btn, index) => (
                  <div
                    key={index}
                    className="relative flex items-center justify-center p-3 border-b last:border-b-0 border-gray-100 group transition-colors hover:bg-white"
                  >
                    <span className="text-[11px] font-bold text-blue-600 flex items-center gap-2">
                      {btn.type === 'quick_reply' && <MousePointer2 size={12} className="text-blue-400" />}
                      {btn.type === 'url' && <ExternalLink size={12} className="text-blue-400" />}
                      {btn.type === 'phone_number' && <Phone size={12} className="text-blue-400" />}
                      {btn.text}
                    </span>
                    
                    {btn.type === 'quick_reply' && (
                      <Handle
                        type="source"
                        position={Position.Right}
                        id={btn.text}
                        className="!w-4 !h-4 !bg-blue-500 !border-[3px] !border-white !-right-2 transition-all hover:scale-125 hover:!bg-blue-600 shadow-md"
                        style={{ top: '50%' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {type === 'list' && (
          <div className="mt-4 p-3 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-between">
            <span className="text-xs font-bold text-indigo-700">
              View Menu List
            </span>
            <List size={14} className="text-indigo-500" />
          </div>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="w-5 h-5 bg-gray-200 border-[4px] border-white shadow-md transition-all hover:!bg-gray-300"
      />

      {/* Default source handle if NOT buttons or template type */}
      {type !== 'buttons' && type !== 'template' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-5 h-5 bg-blue-500 border-[4px] border-white shadow-md transition-all hover:!bg-blue-600 hover:scale-110"
        />
      )}
      
      {/* Template with no buttons needs a default handle */}
      {type === 'template' && (!data.template?.buttons || data.template.buttons.length === 0) && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-5 h-5 bg-blue-500 border-[4px] border-white shadow-md transition-all hover:!bg-blue-600 hover:scale-110"
        />
      )}
    </div>
  );
};

export default MessageNode;
